import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  message,
  Popconfirm,
  Typography,
  Tooltip,
  Select,
  Alert,
  Switch,
  Empty,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import type { ProjectItem } from "../contexts/GlobalContext";
import request from "../utils/request";
import {
  PodcastInfo,
  PodcastListResponse,
  PodcastStatus,
  SPEAKER_TYPES,
  normalizePodcastConfig,
} from "../types/podcast";
import PodcastConfigModal from "../componets/modals/Podcast/PodcastConfigModal";
import PodcastPlayer from "../componets/modals/Podcast/PodcastPlayer";
import KnowledgeEditModal from "../componets/modals/Knowledge/KnowledgeEditModal";
import type { KnowledgeItem } from "../contexts/GlobalContext";

const { Text, Link } = Typography;

const Podcast: React.FC = () => {
  const { API_BASE_URL, token, projects, refreshProjects } = useGlobal();
  const { project: routeProject } = useCurrentProject();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [podcasts, setPodcasts] = useState<PodcastInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<number | undefined>(
    undefined
  );
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [playerVisible, setPlayerVisible] = useState(false);
  const [currentPodcast, setCurrentPodcast] = useState<PodcastInfo | null>(
    null
  );
  const [podcastEnabledProjects, setPodcastEnabledProjects] = useState<
    ProjectItem[]
  >([]);
  const [selectedProjectId, setSelectedProjectId] = useState<
    number | undefined
  >(undefined);
  const [hasConfig, setHasConfig] = useState(false);
  const [summaryDetailVisible, setSummaryDetailVisible] = useState(false);
  const [currentSummary, setCurrentSummary] = useState<KnowledgeItem | null>(
    null
  );
  const [summaryLoading, setSummaryLoading] = useState(false);

  // 获取启用播客的项目列表
  const fetchPodcastEnabledProjects = useCallback(() => {
    if (!projects || projects.length === 0) {
      setPodcastEnabledProjects([]);
      return;
    }

    try {
      // 过滤出启用了播客功能的项目(enable_podcast为true或1)
      const enabledProjects = projects.filter(
        (p: ProjectItem) => !!p.enable_podcast
      );
      setPodcastEnabledProjects(enabledProjects);

      // 如果有启用的项目且没有选中项目,自动选中第一个
      if (enabledProjects.length > 0 && !selectedProjectId) {
        setSelectedProjectId(enabledProjects[0].id);
      }
    } catch (error) {
      console.error("获取项目列表失败:", error);
    }
  }, [projects, selectedProjectId]);

  // 检查是否已配置播客密钥
  const checkPodcastConfig = useCallback(async () => {
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/podcasts/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        normalizePodcastConfig(response.data.data);
        setHasConfig(true);
      }
    } catch (error: any) {
      console.error("检查配置失败:", error);
      setHasConfig(false);
    }
  }, [API_BASE_URL, token]);

  // 初始化时获取项目列表和检查配置
  useEffect(() => {
    fetchPodcastEnabledProjects();
  }, [fetchPodcastEnabledProjects]);

  // 项目作用域：锁定到路由项目（覆盖自动选第一个的兜底）
  useEffect(() => {
    if (routeProject?.id) setSelectedProjectId(routeProject.id);
  }, [routeProject?.id]);

  useEffect(() => {
    if (podcastEnabledProjects.length > 0) {
      checkPodcastConfig();
    }
  }, [podcastEnabledProjects.length, checkPodcastConfig]);

  // 切换项目的播客开关
  const handleTogglePodcast = async (projectId: number, checked: boolean) => {
    try {
      const response = await request.patch(
        `${API_BASE_URL}/api/v3/projects/${projectId}/podcast`,
        {
          enable_podcast: checked,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success(checked ? "已开启AI播客功能" : "已关闭AI播客功能");

        // 刷新全局项目状态，确保首页和播客页面同步
        await refreshProjects();
      } else {
        message.error(response.data.msg || "操作失败");
      }
    } catch (error: any) {
      console.error("切换播客功能失败:", error);
      message.error(error.response?.data?.msg || "操作失败,请重试");
    }
  };

  // 获取播客列表
  const fetchPodcasts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };
      if (statusFilter !== undefined && statusFilter !== -1) {
        params.status = statusFilter;
      }

      const response = await request.get(`${API_BASE_URL}/api/v3/podcasts`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        const data: PodcastListResponse = response.data.data;
        setPodcasts(data.list || []);
        setTotal(data.total || 0);
      } else {
        message.error(response.data.msg || "获取播客列表失败");
      }
    } catch (error: any) {
      console.error("获取播客列表失败:", error);
      message.error(error.response?.data?.msg || "获取播客列表失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, currentPage, pageSize, statusFilter]);

  useEffect(() => {
    if (selectedProjectId && hasConfig) {
      fetchPodcasts();
    }
  }, [fetchPodcasts, selectedProjectId, hasConfig]);

  // 播放播客
  const handlePlay = async (podcast: PodcastInfo) => {
    setCurrentPodcast(podcast);
    setPlayerVisible(true);
  };

  // 删除播客
  const handleDelete = async (podcastId: number) => {
    try {
      const response = await request.delete(
        `${API_BASE_URL}/api/v3/podcasts/${podcastId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("删除成功");
        fetchPodcasts();
      } else {
        message.error(response.data.msg || "删除失败");
      }
    } catch (error: any) {
      console.error("删除失败:", error);
      message.error(error.response?.data?.msg || "删除失败");
    }
  };

  // 查看摘要详情
  const handleGoToSummary = async (summaryId: number) => {
    setSummaryLoading(true);
    try {
      const response = await request.get(
        `${API_BASE_URL}/api/v3/knowledge/${summaryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        if (response.data.data) {
          setCurrentSummary(response.data.data);
          setSummaryDetailVisible(true);
        } else {
          message.error("摘要不存在");
        }
      } else {
        message.error(response.data.msg || "获取摘要详情失败");
      }
    } catch (error: any) {
      console.error("获取摘要详情失败:", error);
      message.error(error.response?.data?.msg || "获取摘要详情失败");
    } finally {
      setSummaryLoading(false);
    }
  };

  // 获取项目名称
  const getProjectName = (projectId: number) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "-";
  };

  // 获取状态标签
  const getStatusTag = (status: number, errorMsg?: string) => {
    switch (status) {
      case PodcastStatus.Pending:
        return <Tag color="default">等待中</Tag>;
      case PodcastStatus.Processing:
        return <Tag color="processing">生成中</Tag>;
      case PodcastStatus.Completed:
        return <Tag color="success">已完成</Tag>;
      case PodcastStatus.Failed:
        return (
          <Tooltip title={errorMsg || "生成失败"}>
            <Tag color="error">失败</Tag>
          </Tooltip>
        );
      default:
        return <Tag>未知</Tag>;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "标题",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: "关联摘要",
      dataIndex: "summary_id",
      key: "summary_id",
      width: 120,
      render: (summaryId: number) => (
        <Link
          onClick={() => handleGoToSummary(summaryId)}
          disabled={summaryLoading}
        >
          查看摘要 #{summaryId}
        </Link>
      ),
    },
    {
      title: "发音人",
      dataIndex: "speaker_type",
      key: "speaker_type",
      width: 100,
      render: (type: string) =>
        SPEAKER_TYPES[type as keyof typeof SPEAKER_TYPES] || type,
    },
    {
      title: "时长",
      dataIndex: "duration",
      key: "duration",
      width: 100,
      render: (duration?: number) => {
        if (!duration) return "-";
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
      },
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: number, record: PodcastInfo) =>
        getStatusTag(status, record.error_msg),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      fixed: "right" as const,
      render: (_: any, record: PodcastInfo) => (
        <Space size="small">
          {record.status === PodcastStatus.Completed && (
            <Button
              type="primary"
              size="small"
              icon={<i className="ri-play-line" />}
              onClick={() => handlePlay(record)}
            >
              播放
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个播客吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      {/* 项目管理卡片 */}
      <Card
        title={
          <Space>
            <i
              className="ri-settings-4-line"
              style={{ color: "#1677ff", fontSize: 18 }}
            />
            AI播客 beta
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {projects && projects.length > 0 ? (
          <div>
            <Alert
              message="选择需要使用AI播客功能的项目"
              description={
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <Text>
                    • 开启"播客功能"开关后，该项目即可使用AI播客生成功能
                  </Text>
                  <Text>• 开启后需配置豆包AI密钥才能正常使用</Text>
                </Space>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {projects.map((project: ProjectItem) => (
                <Card
                  key={project.id}
                  size="small"
                  style={{
                    background: project.enable_podcast ? "#f6ffed" : "#fff",
                  }}
                >
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Space>
                      <i
                        className="ri-apps-line"
                        style={{ fontSize: 16, color: "#1677ff" }}
                      />
                      <Text strong>{project.name}</Text>
                      {project.enable_podcast && (
                        <Tag color="success">已开启</Tag>
                      )}
                    </Space>
                    <Space>
                      <Text type="secondary">播客功能</Text>
                      <Switch
                        checked={!!project.enable_podcast}
                        onChange={(checked) =>
                          handleTogglePodcast(project.id, checked)
                        }
                      />
                    </Space>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        ) : (
          <Empty
            description={
              <Space direction="vertical" size="small">
                <Text>暂无项目，请先创建项目</Text>
                <Button type="primary" onClick={() => navigate("/home")}>
                  去创建项目
                </Button>
              </Space>
            }
          />
        )}
      </Card>

      {/* 播客列表 */}
      {podcastEnabledProjects.length === 0 ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size="small">
                <Text>请先开启项目的AI播客功能</Text>
              </Space>
            }
          />
        </Card>
      ) : !hasConfig ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size="small">
                <Text>请先配置豆包AI密钥</Text>
                <Button
                  type="primary"
                  onClick={() => setConfigModalVisible(true)}
                >
                  立即配置
                </Button>
              </Space>
            }
          />
        </Card>
      ) : (
        <Card
          title={
            <Space>
              <i
                className="ri-mic-line"
                style={{ color: "#1677ff", fontSize: 18 }}
              />
              我的播客
            </Space>
          }
          extra={
            <Space>
              <Select
                style={{ width: 120 }}
                placeholder="筛选状态"
                allowClear
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value);
                  setCurrentPage(1);
                }}
              >
                <Select.Option value={-1}>全部</Select.Option>
                <Select.Option value={PodcastStatus.Pending}>
                  等待中
                </Select.Option>
                <Select.Option value={PodcastStatus.Processing}>
                  生成中
                </Select.Option>
                <Select.Option value={PodcastStatus.Completed}>
                  已完成
                </Select.Option>
                <Select.Option value={PodcastStatus.Failed}>失败</Select.Option>
              </Select>
              <Button
                type="primary"
                icon={<i className="ri-settings-3-line" />}
                onClick={() => setConfigModalVisible(true)}
              >
                播客配置
              </Button>
              <Button
                icon={<i className="ri-refresh-line" />}
                onClick={fetchPodcasts}
              >
                刷新
              </Button>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={podcasts}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      <PodcastConfigModal
        visible={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onSuccess={() => {
          checkPodcastConfig();
          fetchPodcasts();
        }}
      />

      <PodcastPlayer
        visible={playerVisible}
        onCancel={() => {
          setPlayerVisible(false);
          setCurrentPodcast(null);
        }}
        podcast={currentPodcast}
      />

      <KnowledgeEditModal
        visible={summaryDetailVisible}
        onCancel={() => {
          setSummaryDetailVisible(false);
          setCurrentSummary(null);
        }}
        currentRecord={currentSummary}
        onSuccess={() => {
          setSummaryDetailVisible(false);
          setCurrentSummary(null);
          // 可选：刷新播客列表
          fetchPodcasts();
        }}
        getProjectName={getProjectName}
      />
    </div>
  );
};

export default Podcast;
